import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import CFColorPicker from "../shared/forms/CFColorPicker";
import Tooltip from "../shared/Tooltip";

const SWATCH_HEIGHT = 22;

function WebsiteBrandRemixThemeColors(rawProps) {
  const website = rawProps.website;
  const variant = rawProps.variant;
  const vibrantDefault = rawProps.vibrant_default ?? rawProps.vibrantDefault;
  const lightBase = rawProps.light_base ?? rawProps.lightBase;
  const darkBase = rawProps.dark_base ?? rawProps.darkBase;
  const initialBackground =
    rawProps.initial_background ?? rawProps.initialBackground;
  const initialHeading = rawProps.initial_heading ?? rawProps.initialHeading;
  const initialParagraph =
    rawProps.initial_paragraph ?? rawProps.initialParagraph;
  const initialButton = rawProps.initial_button ?? rawProps.initialButton;
  const initialButtonText =
    rawProps.initial_button_text ?? rawProps.initialButtonText;
  const initialAccent = rawProps.initial_accent ?? rawProps.initialAccent;

  const isLight = variant === "light";
  /* Dark variant: matches builder/remix panel (slate-950 + dark: inputs) */
  const previewWrapClass = isLight
    ? "rounded-xl border-solid smooth border border-slate-200 p-2.5 mb-4 bg-white"
    : "rounded-xl border border-slate-700 bg-slate-900 p-2.5 mb-4";

  const [colors, setColors] = useState({
    bg: initialBackground,
    heading: initialHeading,
    paragraph: initialParagraph,
    button: initialButton,
    buttonText: initialButtonText,
    accent: initialAccent,
  });

  const prevLightBase = useRef(lightBase);
  const prevDarkBase = useRef(darkBase);
  const prevVibrant = useRef(vibrantDefault);

  const syncFromTopForm = useCallback(
    (e) => {
      const name = e.target?.name || "";
      const val = e.target?.value;
      if (val == null || typeof val !== "string") return;

      if (isLight && name.includes("brand_color_light")) {
        const pv = prevLightBase.current;
        setColors((c) => (c.bg === pv ? { ...c, bg: val } : c));
        prevLightBase.current = val;
        return;
      }
      if (!isLight && name.includes("brand_color_dark")) {
        const pv = prevDarkBase.current;
        setColors((c) => (c.bg === pv ? { ...c, bg: val } : c));
        prevDarkBase.current = val;
        return;
      }
      if (name.includes("brand_color_vibrant")) {
        const pv = prevVibrant.current;
        setColors((c) => {
          const next = { ...c };
          if (c.button === pv) next.button = val;
          if (c.accent === pv) next.accent = val;
          return next;
        });
        prevVibrant.current = val;
      }
    },
    [isLight]
  );

  useEffect(() => {
    document.addEventListener("input", syncFromTopForm, true);
    document.addEventListener("change", syncFromTopForm, true);
    return () => {
      document.removeEventListener("input", syncFromTopForm, true);
      document.removeEventListener("change", syncFromTopForm, true);
    };
  }, [syncFromTopForm]);

  const prefix = isLight ? "brand_remix_light" : "brand_remix_dark";
  const field = (key) => `website[data][${prefix}_${key}]`;

  const setField = (key, value) => {
    setColors((c) => ({ ...c, [key]: value }));
  };

  const strip = [
    colors.bg,
    colors.heading,
    colors.paragraph,
    colors.button,
    colors.buttonText,
    colors.accent,
  ];

  const labelClass = isLight
    ? "field-title"
    : "setting-label text-xl text-slate-300 capitalize";

  return (
    <div
      className={`website-brand-remix-theme-colors ${
        !isLight ? "text-slate-200" : ""
      }`}
    >
      <h5
        className={`mb-2.5 font-semibold smooth ${
          isLight ? "field-title" : "text-[18px] text-slate-200"
        }`}
        style={{
          display: rawProps.hideTitle ? "none" : "block",
          ...(isLight ? { fontSize: 16 } : {}),
        }}
      >
        Colors
      </h5>
      <div
        className={previewWrapClass}
        role="img"
        aria-label="Color theme preview"
      >
        <div className="flex gap-0.5 overflow-hidden rounded-lg">
          {strip.map((hex, i) => (
            <div
              key={`swatch-${i}`}
              className="flex-1 min-w-[20px] rounded-sm"
              style={{
                height: SWATCH_HEIGHT,
                backgroundColor: hex && String(hex).trim() ? hex : "#e5e7eb",
              }}
            />
          ))}
        </div>
      </div>

      <div
        className="row hard-center"
        style={{ marginLeft: -5, marginRight: -5 }}
      >
        <div
          className="col-xs-6 form-inputs color-field"
          style={{ paddingRight: 5 }}
        >
          <span className={labelClass}>Heading Color</span>
          <CFColorPicker
            name={field("heading_font_color")}
            placeholder="#000000"
            website={website}
            value={colors.heading}
            onChange={(v) => setField("heading", v)}
          />
        </div>
        <div
          className="col-xs-6 form-inputs color-field"
          style={{ paddingRight: 5 }}
        >
          <span className={labelClass}>Paragraph Color</span>
          <CFColorPicker
            name={field("paragraph_font_color")}
            placeholder="#000000"
            website={website}
            value={colors.paragraph}
            onChange={(v) => setField("paragraph", v)}
          />
        </div>
        <div
          className="col-xs-6 form-inputs color-field some-vertical-space"
          style={{ paddingRight: 5 }}
        >
          <span className={labelClass}>
            Buttons{" "}
            <Tooltip
              tooltip="Button fill color. Defaults to vibrant."
              inline
              onDarkBackground={!isLight}
            />
          </span>
          <CFColorPicker
            name={field("button_color")}
            placeholder={vibrantDefault}
            website={website}
            value={colors.button}
            onChange={(v) => setField("button", v)}
          />
        </div>
        <div
          className="col-xs-6 form-inputs color-field some-vertical-space"
          style={{ paddingRight: 5 }}
        >
          <span className={labelClass}>
            Button Text{" "}
            <Tooltip
              tooltip="Text color on primary buttons. Defaults to white."
              inline
              onDarkBackground={!isLight}
            />
          </span>
          <CFColorPicker
            name={field("button_text_color")}
            placeholder="#ffffff"
            website={website}
            value={colors.buttonText}
            onChange={(v) => setField("buttonText", v)}
          />
        </div>
        <div
          className="col-xs-6 form-inputs color-field some-vertical-space"
          style={{ paddingRight: 5 }}
        >
          <span className={labelClass}>
            Accents{" "}
            <Tooltip
              tooltip="Accent highlights. Defaults to vibrant."
              inline
              onDarkBackground={!isLight}
            />
          </span>
          <CFColorPicker
            name={field("accent_color")}
            placeholder={vibrantDefault}
            website={website}
            value={colors.accent}
            onChange={(v) => setField("accent", v)}
          />
        </div>
        <div
          className="col-xs-12 form-inputs color-field some-vertical-space"
          style={{ paddingRight: 5 }}
        >
          <span className={labelClass}>
            Background{" "}
            <Tooltip
              tooltip={
                isLight
                  ? "Page background for the light remix preset. Defaults to your Light color."
                  : "Page background for the dark remix preset. Defaults to your Dark color."
              }
              inline
              onDarkBackground={!isLight}
            />
          </span>
          <CFColorPicker
            name={field("background_color")}
            placeholder={isLight ? lightBase : darkBase}
            website={website}
            value={colors.bg}
            onChange={(v) => setField("bg", v)}
          />
        </div>
      </div>
    </div>
  );
}

WebsiteBrandRemixThemeColors.propTypes = {
  website: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(["light", "dark"]).isRequired,
  vibrantDefault: PropTypes.string.isRequired,
  lightBase: PropTypes.string.isRequired,
  darkBase: PropTypes.string.isRequired,
  initialBackground: PropTypes.string.isRequired,
  initialHeading: PropTypes.string.isRequired,
  initialParagraph: PropTypes.string.isRequired,
  initialButton: PropTypes.string.isRequired,
  initialButtonText: PropTypes.string.isRequired,
  initialAccent: PropTypes.string.isRequired,
  hideTitle: PropTypes.bool,
};

export default WebsiteBrandRemixThemeColors;
